import { useState, useEffect, useMemo } from "react";
import { ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";
import { api } from "@/lib/api";

const SLOTS = [
  ["08:00", "8:00 AM"],
  ["10:00", "10:00 AM"],
  ["12:30", "12:30 PM"],
  ["14:30", "2:30 PM"],
  ["16:30", "4:30 PM"],
];

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DOW = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

export const pad2 = (n) => String(n).padStart(2, "0");
export const toDateKey = (y, m, d) => `${y}-${pad2(m + 1)}-${pad2(d)}`;
export const MONTH_NAMES = MONTHS;
export const DOW_LABELS = DOW;

const pad = pad2;
const toKey = toDateKey;

const AvailabilityPicker = ({ date, time, onChange }) => {
  const today = new Date();
  const [view, setView] = useState({ y: today.getFullYear(), m: today.getMonth() });
  const [taken, setTaken] = useState({});
  const [blocked, setBlocked] = useState([]);

  const monthKey = `${view.y}-${pad(view.m + 1)}`;

  useEffect(() => {
    api.get(`/availability?month=${monthKey}`)
      .then((r) => {
        setTaken(r.data.taken || {});
        setBlocked(r.data.blocked || []);
      })
      .catch(() => {});
  }, [monthKey]);

  const cells = useMemo(() => {
    const first = new Date(view.y, view.m, 1);
    const lead = (first.getDay() + 6) % 7;
    const days = new Date(view.y, view.m + 1, 0).getDate();
    const out = [];
    for (let i = 0; i < lead; i++) out.push(null);
    for (let d = 1; d <= days; d++) out.push(d);
    return out;
  }, [view]);

  const canPrev = view.y > today.getFullYear() || view.m > today.getMonth();
  const move = (dir) =>
    setView((v) => {
      const d = new Date(v.y, v.m + dir, 1);
      return { y: d.getFullYear(), m: d.getMonth() };
    });

  const dayState = (d) => {
    if (!d) return "blank";
    const key = toKey(view.y, view.m, d);
    const cellDate = new Date(view.y, view.m, d);
    const isPast = cellDate < new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const isSunday = cellDate.getDay() === 0;
    const full = (taken[key] || []).length >= SLOTS.length;
    const isBlocked = blocked.includes(key);
    if (isPast || isSunday || full || isBlocked) return "closed";
    if (key === date) return "selected";
    return "open";
  };

  const takenForSelected = taken[date] || [];

  return (
    <div data-testid="availability-calendar" className="bg-carbon border border-edge rounded-xl p-4 sm:p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 font-syne font-bold text-sm uppercase tracking-widest text-slate-100">
          <CalendarDays size={15} className="text-volt" />
          {MONTHS[view.m]} {view.y}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            data-testid="cal-prev-month"
            onClick={() => move(-1)}
            disabled={!canPrev}
            aria-label="Previous month"
            className="w-8 h-8 rounded-full border border-edge flex items-center justify-center text-slate-300 hover:border-volt hover:text-volt disabled:opacity-30 disabled:hover:border-edge disabled:hover:text-slate-300 transition-colors"
          >
            <ChevronLeft size={14} />
          </button>
          <button
            type="button"
            data-testid="cal-next-month"
            onClick={() => move(1)}
            aria-label="Next month"
            className="w-8 h-8 rounded-full border border-edge flex items-center justify-center text-slate-300 hover:border-volt hover:text-volt transition-colors"
          >
            <ChevronRight size={14} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1 text-center mb-1.5">
        {DOW.map((d) => (
          <div key={d} className="font-mono2 text-[9px] uppercase tracking-widest text-slate-600 py-1">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          const state = dayState(d);
          if (state === "blank") return <div key={`b${i}`} />;
          const key = toKey(view.y, view.m, d);
          return (
            <button
              key={key}
              type="button"
              data-testid={`cal-day-${key}`}
              disabled={state === "closed"}
              onClick={() => onChange(key, "")}
              className={`aspect-square rounded-lg font-mono2 text-xs transition-colors duration-200 flex items-center justify-center ${
                state === "selected"
                  ? "bg-volt text-carbon font-bold"
                  : state === "closed"
                    ? "text-slate-700 line-through decoration-slate-700/60 cursor-not-allowed"
                    : "text-slate-300 border border-edge/60 hover:border-volt hover:text-volt"
              }`}
            >
              {d}
            </button>
          );
        })}
      </div>

      {date && (
        <div data-testid="slot-picker" className="mt-5 pt-4 border-t border-edge">
          <div className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-400 mb-3">
            Available times · {date.split("-").reverse().join("/")}
          </div>
          <div className="flex flex-wrap gap-2">
            {SLOTS.map(([value, label]) => {
              const isTaken = takenForSelected.includes(value);
              const isSel = time === value;
              return (
                <button
                  key={value}
                  type="button"
                  data-testid={`slot-chip-${value.replace(":", "")}`}
                  disabled={isTaken}
                  onClick={() => onChange(date, value)}
                  className={`rounded-full px-4 py-2 font-mono2 text-xs tracking-wider transition-colors duration-200 ${
                    isSel
                      ? "bg-volt text-carbon font-bold"
                      : isTaken
                        ? "border border-edge/50 text-slate-700 line-through cursor-not-allowed"
                        : "border border-edge text-slate-300 hover:border-volt hover:text-volt"
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-4 flex items-center gap-4 font-mono2 text-[9px] uppercase tracking-widest text-slate-600">
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm border border-edge" /> Open</span>
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm bg-volt" /> Selected</span>
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm text-slate-700 line-through">×</span> Full / Closed</span>
      </div>
    </div>
  );
};

export default AvailabilityPicker;
